// src/components/Control/ControlPanel/PersonOverlay.tsx
// Люди на камере 1 (robot/<id>/person, person_node на Pi): рамки и скелет поверх
// видео. Холст занимает весь блок видео (кадр 4:3, вписан так же, как <img> с
// object-fit: contain) и подстраивается под его размер.
import React, { useEffect, useRef } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';

interface Person {
  id?: number;
  held?: boolean;  // no detection this frame, kept by the tracker
  known?: { name: string; similarity: number } | null;  // recognised by clothes
  box: [number, number, number, number];
  score: number;
  fill: number;
  hands_up: boolean;
  kpts: [number, number, number][];
}

// COCO skeleton (keypoint indices)
const BONES: [number, number][] = [
  [5, 6], [5, 7], [7, 9], [6, 8], [8, 10], [5, 11], [6, 12], [11, 12],
  [11, 13], [13, 15], [12, 14], [14, 16], [0, 1], [0, 2], [1, 3], [2, 4],
];
const STALE_MS = 1000;

const PersonOverlay: React.FC<{ zenohSession: zenoh.Session | null; robotId: string }> = ({ zenohSession, robotId }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastRef = useRef<{ width: number; height: number; people: Person[]; at: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!zenohSession || !canvas || !ctx) return;
    let cancelled = false;
    let sub: zenoh.Subscriber | null = null;
    let clearTimer: ReturnType<typeof setTimeout> | null = null;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      const msg = lastRef.current;
      if (!msg || Date.now() - msg.at > STALE_MS) return;
      const s = Math.min(width / msg.width, height / msg.height);
      const ox = (width - msg.width * s) / 2;
      const oy = (height - msg.height * s) / 2;
      const font = Math.max(11, Math.round(width / 32));
      msg.people.forEach((p, i) => {
        const [x1, y1, x2, y2] = p.box;
        const color = i === 0 ? (p.hands_up ? '#e74c3c' : '#2ecc71') : '#f1c40f';
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.setLineDash(p.held ? [5, 4] : []);
        ctx.strokeRect(ox + x1 * s, oy + y1 * s, (x2 - x1) * s, (y2 - y1) * s);
        ctx.setLineDash([]);
        ctx.fillStyle = color;
        ctx.font = `${font}px sans-serif`;
        ctx.fillText(`${p.known ? `${p.known.name} ` : p.id != null ? `#${p.id} ` : ''}${Math.round(p.fill * 100)}%${p.hands_up ? ' руки вверх' : ''}`,
                     ox + x1 * s + 2, oy + y1 * s + font + 1);
        ctx.lineWidth = 1.5;
        for (const [a, b] of BONES) {
          const pa = p.kpts[a];
          const pb = p.kpts[b];
          if (!pa || !pb || pa[2] < 0.5 || pb[2] < 0.5) continue;
          ctx.beginPath();
          ctx.moveTo(ox + pa[0] * s, oy + pa[1] * s);
          ctx.lineTo(ox + pb[0] * s, oy + pb[1] * s);
          ctx.stroke();
        }
      });
      if (clearTimer) clearTimeout(clearTimer);
      clearTimer = setTimeout(draw, STALE_MS);
    };

    const ro = new ResizeObserver(draw);
    ro.observe(canvas);

    (async () => {
      try {
        const s = await zenohSession.declareSubscriber(`robot/${robotId}/person`, {
          handler: (sample: zenoh.Sample) => {
            try {
              lastRef.current = { ...JSON.parse(sample.payload().toString()), at: Date.now() };
              draw();
            } catch { /* ignore */ }
          },
        });
        if (cancelled) await s.undeclare();
        else sub = s;
      } catch (err) {
        console.warn('PersonOverlay: subscribe failed', err);
      }
    })();
    return () => {
      cancelled = true;
      ro.disconnect();
      if (clearTimer) clearTimeout(clearTimer);
      sub?.undeclare().catch(() => undefined);
    };
  }, [zenohSession, robotId]);

  return (
    <canvas ref={canvasRef} aria-hidden="true"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
  );
};

export default PersonOverlay;
